import { Input as HeroUiInput } from '@heroui/react';
import { type FC, memo } from 'react';
import { twMerge } from 'tailwind-merge';
import { Controller, useFormContext } from 'FrontRoyalReactHookForm';

type VanillaInputProps = {
    value?: string;
    placeholder?: string;
    type?: 'text' | 'email' | 'password' | 'search' | 'url' | 'tel' | 'number';
    label?: string;
    disabled?: boolean;
    startContent?: React.ReactNode;
    classNames?: Record<string, string>;
    isInvalid?: boolean;
    errorMessage?: string;
    // Marks the label with an asterisk, as HeroUI does for required fields.
    isRequired?: boolean;
    // Shows a clear button when the input has a value.
    isClearable?: boolean;
    onClear?: () => void;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
};

const VanillaInput: FC<VanillaInputProps> = memo(
    ({
        value,
        placeholder,
        type = 'text',
        label,
        disabled,
        startContent,
        classNames,
        isInvalid,
        errorMessage,
        isRequired,
        isClearable,
        onClear,
        onChange,
        onBlur,
        onKeyDown,
    }) => (
        <HeroUiInput
            value={value}
            placeholder={placeholder}
            type={type}
            label={label}
            labelPlacement="inside"
            isDisabled={disabled}
            startContent={startContent}
            isInvalid={isInvalid}
            errorMessage={errorMessage}
            isRequired={isRequired}
            isClearable={isClearable}
            onClear={onClear}
            // Report our own validation state through ARIA rather than pushing it into native
            // constraint validation. (The browser's intrinsic validation for a constrained `type`
            // — email, url, number — is unaffected by this; no consumer passes one today.)
            //
            // HeroUI resolves this to "native" when unset (use-input: formValidationBehavior ??
            // globalContext?.validationBehavior ?? "native"), overriding react-aria's own 'aria'
            // default. Under "native", passing isInvalid makes react-aria call setCustomValidity()
            // on the underlying <input>, so form.checkValidity() goes false and the browser refuses
            // to submit — silently. No submit event, so no react-hook-form handler, no request, and
            // no feedback: the submit button stays enabled because RHF's own isValid is untouched.
            // Recovery required remounting the input, i.e. closing and reopening the form.
            //
            // That bites whenever isInvalid reflects something the user can retry without editing
            // the field — a server-side rejection they fixed elsewhere, say. Validation in this
            // codebase is react-hook-form + yup, which gates submission itself and never relies on
            // the native path, so 'aria' is the correct behavior for every consumer.
            validationBehavior="aria"
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            classNames={{
                label: twMerge(
                    'text-sm font-normal text-black group-data-[filled-within=true]:text-black data-[focused=true]:text-black data-[focused=true]:mt-2 group-data-[filled-within=true]:mt-2',
                    classNames?.label || '',
                ),
                inputWrapper: twMerge(
                    'bg-slate-grey-50 hover:bg-slate-grey-100 rounded-7',
                    classNames?.inputWrapper || '',
                ),
                input: twMerge('text-slate-grey group-data-[has-value=true]:text-slate-grey', classNames?.input || ''),
                errorMessage: 'text-red-500',
                // HeroUI's default foreground is a dark plum. Match the slate grey of the input text.
                clearButton: twMerge('text-slate-grey', classNames?.clearButton || ''),
            }}
        />
    ),
);

type InputProps = {
    name: string;
    label?: string;
    placeholder?: string;
    type?: 'text' | 'email' | 'password' | 'search' | 'url' | 'tel' | 'number';
    disabled?: boolean;
    startContent?: React.ReactNode;
    classNames?: Record<string, string>;
    isInvalid?: boolean;
    errorMessage?: string;
    isRequired?: boolean;
    isClearable?: boolean;
    onClear?: () => void;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
};

export const Input: FC<InputProps> = memo(
    ({ name, onChange, onBlur, onClear, onKeyDown, isInvalid, errorMessage, ...inputProps }) => {
        const { control } = useFormContext();

        return (
            <Controller
                control={control}
                name={name}
                render={({ field }) => (
                    <VanillaInput
                        {...inputProps}
                        {...field}
                        isInvalid={isInvalid}
                        errorMessage={errorMessage}
                        onChange={e => {
                            field.onChange(e);
                            onChange?.(e);
                        }}
                        onBlur={e => {
                            field.onBlur();
                            onBlur?.(e);
                        }}
                        // HeroUI shows the clear button whenever onClear is set, so pass it only when asked
                        onClear={
                            inputProps.isClearable || onClear
                                ? () => {
                                      field.onChange('');
                                      onClear?.();
                                  }
                                : undefined
                        }
                        onKeyDown={onKeyDown}
                    />
                )}
            />
        );
    },
);
